/**
 * The token, shown once, with the steps that put it to use.
 *
 * The server keeps only the token's digest, so this dialog is the one place
 * the whole token ever appears. It says so, offers a copy button, and lists
 * the install steps where the person needs them: right next to the thing
 * they are about to paste.
 */
import { Copy, KeyRound } from "lucide-react";
import { Input } from "@wa/ui";
import { Btn, Modal, useCopyToast } from "../../../ui";
import type { T } from "../words";

const STEPS = ["step1", "step2", "step3", "step4"] as const;

export function TokenDialog({ token, t, onClose }: { token: string; t: T; onClose: () => void }) {
  const copy = useCopyToast();

  return (
    <Modal
      title={
        <span className="inline-flex items-center gap-2">
          <KeyRound className="size-4" strokeWidth={1.8} />
          {t("monitoring.reporter.token.title")}
        </span>
      }
      onClose={onClose}
      label={t("monitoring.reporter.token.title")}
      footer={
        <>
          <Btn onClick={() => copy(token)}>
            <Copy className="size-[15px]" strokeWidth={1.8} />
            {t("monitoring.reporter.token.copy")}
          </Btn>
          <Btn tone="dark" onClick={onClose}>
            {t("monitoring.reporter.token.done")}
          </Btn>
        </>
      }
    >
      <div className="space-y-4 px-[26px] pb-2 text-[13px]">
        <p className="text-[var(--color-warning)]">{t("monitoring.reporter.token.once")}</p>
        <Input
          readOnly
          value={token}
          aria-label={t("monitoring.reporter.token.title")}
          className="font-mono text-[12px]"
          onFocus={(e) => e.currentTarget.select()}
        />
        <div>
          <p className="crm-prow-n mb-1.5">{t("monitoring.reporter.token.steps")}</p>
          <ol className="crm-sub list-decimal space-y-1 pl-5">
            {STEPS.map((step) => (
              <li key={step}>{t(`monitoring.reporter.token.${step}`)}</li>
            ))}
          </ol>
        </div>
      </div>
    </Modal>
  );
}
